export const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export function prettyDate(ymd) {
  if (!ymd) return "";
  const local = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const today = new Date();
  const t = local(today);
  const y = local(new Date(today.getTime() - 86400000));
  if (ymd === t) return "Today";
  if (ymd === y) return "Yesterday";
  const d = new Date(ymd + "T00:00:00");
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

// Backend datetimes come back from Mongo WITHOUT a timezone offset (e.g.
// "2026-09-21T13:08:08.500000") but are actually UTC. Append "Z" so the browser
// doesn't misread them as local time. Only applies to full timestamps.
export function toDate(iso) {
  if (iso instanceof Date) return iso;
  if (typeof iso === "string" && !/([zZ]|[+-]\d{2}:?\d{2})$/.test(iso)) return new Date(iso + "Z");
  return new Date(iso);
}

// Workspace timezone (IANA), cached module-wide so timestamps render consistently.
let _companyTz = "UTC";
export const getCompanyTz = () => _companyTz;
export const setCompanyTz = (tz) => { if (tz) _companyTz = tz; };
export async function fetchCompanyTz() {
  try {
    const { api } = await import("@/lib/api");
    const c = await api.get("/company");
    if (c && c.timezone) _companyTz = c.timezone;
  } catch {
    /* keep last known */
  }
  return _companyTz;
}

export function fmtStamp(iso, tz) {
  if (!iso) return "";
  const d = toDate(iso);
  if (isNaN(d.getTime())) return "";
  const opts = { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" };
  try {
    return d.toLocaleString(undefined, { ...opts, timeZone: tz || _companyTz });
  } catch {
    return d.toLocaleString(undefined, opts);
  }
}

// Full date + time (with year) in the workspace timezone — for PDF exports / audit lines.
export function fmtDateTime(iso, tz) {
  if (!iso) return "";
  const d = toDate(iso);
  if (isNaN(d.getTime())) return "";
  const opts = { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" };
  try {
    return d.toLocaleString(undefined, { ...opts, timeZone: tz || _companyTz });
  } catch {
    return d.toLocaleString(undefined, opts);
  }
}

// ISO week number (mirrors mobile week labels).
export function isoWeek(ymd) {
  const d = new Date(ymd + "T00:00:00");
  const target = new Date(d.valueOf());
  const dayNr = (d.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = target.valueOf();
  target.setMonth(0, 1);
  if (target.getDay() !== 4) target.setMonth(0, 1 + ((4 - target.getDay() + 7) % 7));
  return 1 + Math.ceil((firstThursday - target) / 604800000);
}

export const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ISO week key like "2026-W23" from a YYYY-MM-DD date string.
export function isoWeekKey(dateStr) {
  const dt = new Date(dateStr + "T00:00:00");
  const d = new Date(Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const wk = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(wk).padStart(2, "0")}`;
}

// Monday date (YYYY-MM-DD) for the ISO week that contains dateStr.
export function mondayOf(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function bucketKey(dateStr, mode) {
  if (!dateStr) return "";
  if (mode === "month") return dateStr.slice(0, 7);
  if (mode === "week") return isoWeekKey(dateStr);
  return dateStr;
}

export function bucketLabel(key, mode) {
  if (mode === "month") {
    const [y, m] = key.split("-");
    return new Date(+y, +m - 1, 1).toLocaleDateString(undefined, { month: "short", year: "numeric" });
  }
  if (mode === "week") return key.replace("-W", " · Wk ");
  return prettyDate(key);
}

// Broad fallback list (major IANA zones) for engines without Intl.supportedValuesOf.
const TZ_FALLBACK = [
  "UTC",
  "Africa/Abidjan", "Africa/Accra", "Africa/Addis_Ababa", "Africa/Algiers", "Africa/Cairo", "Africa/Casablanca",
  "Africa/Johannesburg", "Africa/Lagos", "Africa/Nairobi", "Africa/Tripoli", "Africa/Tunis",
  "America/Anchorage", "America/Argentina/Buenos_Aires", "America/Bogota", "America/Caracas", "America/Chicago",
  "America/Denver", "America/Edmonton", "America/Halifax", "America/Havana", "America/Lima", "America/Los_Angeles",
  "America/Mexico_City", "America/New_York", "America/Phoenix", "America/Santiago", "America/Sao_Paulo",
  "America/St_Johns", "America/Toronto", "America/Vancouver", "America/Winnipeg",
  "Asia/Almaty", "Asia/Amman", "Asia/Baghdad", "Asia/Baku", "Asia/Bangkok", "Asia/Beirut", "Asia/Colombo",
  "Asia/Dhaka", "Asia/Dubai", "Asia/Hong_Kong", "Asia/Istanbul", "Asia/Jakarta", "Asia/Jerusalem", "Asia/Kabul",
  "Asia/Karachi", "Asia/Kathmandu", "Asia/Kolkata", "Asia/Kuala_Lumpur", "Asia/Kuwait", "Asia/Manila",
  "Asia/Muscat", "Asia/Qatar", "Asia/Riyadh", "Asia/Seoul", "Asia/Shanghai", "Asia/Singapore", "Asia/Taipei",
  "Asia/Tashkent", "Asia/Tehran", "Asia/Tokyo", "Asia/Yangon", "Asia/Yekaterinburg",
  "Atlantic/Azores", "Atlantic/Cape_Verde", "Atlantic/Reykjavik",
  "Australia/Adelaide", "Australia/Brisbane", "Australia/Darwin", "Australia/Melbourne", "Australia/Perth", "Australia/Sydney",
  "Europe/Amsterdam", "Europe/Athens", "Europe/Belgrade", "Europe/Berlin", "Europe/Brussels", "Europe/Bucharest",
  "Europe/Budapest", "Europe/Copenhagen", "Europe/Dublin", "Europe/Helsinki", "Europe/Istanbul", "Europe/Kyiv",
  "Europe/Lisbon", "Europe/London", "Europe/Madrid", "Europe/Moscow", "Europe/Oslo", "Europe/Paris", "Europe/Prague",
  "Europe/Rome", "Europe/Stockholm", "Europe/Vienna", "Europe/Warsaw", "Europe/Zurich",
  "Pacific/Auckland", "Pacific/Fiji", "Pacific/Guam", "Pacific/Honolulu", "Pacific/Port_Moresby",
];

// All IANA timezone names — full list when the engine supports it, else a broad fallback.
export function allTimezones() {
  try {
    const fn = Intl.supportedValuesOf;
    if (typeof fn === "function") {
      const list = fn("timeZone");
      if (Array.isArray(list) && list.length) return list;
    }
  } catch {
    /* fall through */
  }
  return TZ_FALLBACK;
}
